import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Calendar, FileText, User, HelpCircle, History } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';

export default function AdminAuditSectionDetails() {
  const { sectionId } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();

  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSectionDetails();
  }, [sectionId]);

  const fetchSectionDetails = async () => {
    setLoading(true);
    
    const result = await adminAuditService.getAuditSectionDetail(authTokens.access, sectionId);
    
    if (result.success) {
      setSection(result.data);
    } else {
      showError(result.error || 'Erreur lors du chargement des détails de la section');
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la section...</p>
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Section d'audit non trouvée</p>
        <Link to="/dashboard/admin/audits/plans" className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux plans d'audit
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/admin/audits/plans/${section.audit_plan?.id}/sections`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{section.section_name}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails de la section d'audit</p>
          </div>
        </div>
        <Link
          to={`/dashboard/admin/audits/sections/${sectionId}/history`}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
        >
          <History className="h-4 w-4 mr-2" />
          Historique
        </Link>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Section Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de la section</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Nom de la section</p>
                <p className="text-sm text-gray-900">{section.section_name}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-green-600" />
                  Localisation
                </p>
                <p className="text-sm text-gray-900">{section.location || 'Non défini'}</p>
              </div>
            </div>
          </div>

          {/* Audit Plan Information */}
          {section.audit_plan && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" />
                Plan d'audit
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Titre</p>
                  <p className="text-sm text-gray-900 mt-1">{section.audit_plan.title}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Référence</p>
                  <p className="text-sm text-gray-900 mt-1">{section.audit_plan.reference || 'Non défini'}</p>
                </div>
                <div className="pt-2">
                  <Link
                    to={`/dashboard/admin/audits/plans/${section.audit_plan.id}`}
                    className="text-sm text-green-600 hover:text-green-700 hover:underline"
                  >
                    Voir les détails du plan →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-green-600" />
              Métadonnées
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600">Créé par</p>
                <p className="text-sm text-gray-900 mt-1 flex items-center">
                  <User className="h-4 w-4 mr-1 text-gray-500" />
                  {section.created_by_name || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Date de création</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(section.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Dernière mise à jour</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(section.updated_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              {section.last_updated_by_name && (
                <div>
                  <p className="text-xs text-gray-600">Dernière modification par</p>
                  <p className="text-sm text-gray-900 mt-1 flex items-center">
                    <User className="h-4 w-4 mr-1 text-gray-500" />
                    {section.last_updated_by_name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Questions Count */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Statistiques</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Questions d'audit</span>
                <span className="text-2xl font-bold text-blue-600">{section.questions_count || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Audit Questions Link */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <HelpCircle className="h-5 w-5 mr-2 text-green-600" />
              Questions d'audit
            </h3>
            <p className="text-sm text-gray-600 mt-1">Consultez les questions d'audit de cette section</p>
          </div>
          <Link
            to={`/dashboard/admin/audits/sections/${section.id}/questions`}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Voir les questions
          </Link>
        </div>
      </div>
    </div>
  );
}
